/**
 * Trust Center hub — tenant-facing landing that aggregates nine trust
 * posture surfaces (attestations, DPA, sub-processors, encryption,
 * residency, DPF, GDPR/CCPA DSRs, pentest) into a 3-column card grid.
 *
 * Data layer: TanStack Query, single query that fans out ten parallel
 * GETs via `fetchTrustHub`. Individual card failures fall through as
 * "No data yet" so the hub keeps rendering.
 *
 * Style: emerald→sky `text-brand-gradient` headline, Inter, zinc
 * borders, 6-8px rounded. Light theme only.
 */

import { useQuery } from "@tanstack/react-query";
import {
	buildCards,
	fetchTrustHub,
	TRUST_CARD_META,
	type TrustCardView,
	trustHubQueryKeys,
} from "./api";

// ---------------------------------------------------------------------------
// Local style tokens — kept inline so the surface stays readable at a
// glance and matches the brand rules without touching shared surfaces.
// ---------------------------------------------------------------------------

const CARD =
	"flex flex-col gap-3 rounded-[8px] border border-zinc-200 bg-white p-5 shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const CARD_PENDING =
	"flex flex-col gap-3 rounded-[8px] border border-dashed border-zinc-200 bg-zinc-50 p-5";
const FIELD_LABEL = "text-[11px] font-medium uppercase tracking-[0.1em] text-zinc-500";
const FIELD_VALUE = "text-sm font-medium text-zinc-900";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function TrustCenterPage() {
	const query = useQuery({
		queryKey: trustHubQueryKeys.root,
		queryFn: fetchTrustHub,
		staleTime: 60_000,
	});

	const pending = query.isLoading;
	const error = query.isError ? describeError(query.error) : null;
	const cards: TrustCardView[] = query.data ? buildCards(query.data) : [];

	return (
		<main className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10 sm:py-16">
			<Breadcrumb />
			<Heading />

			{error && <ErrorBanner message={error} />}

			<section aria-label="Trust Center hub" className="flex flex-col gap-4">
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
					{pending
						? TRUST_CARD_META.map((meta) => (
								<PendingCard
									key={meta.id}
									title={meta.title}
									description={meta.description}
								/>
							))
						: cards.map((card) => <Card key={card.id} card={card} />)}
				</div>
			</section>

			<FooterNote />
		</main>
	);
}

export default TrustCenterPage;

// ---------------------------------------------------------------------------
// Presentational fragments
// ---------------------------------------------------------------------------

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<a
						href="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</a>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Compliance</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Trust Center</li>
			</ol>
		</nav>
	);
}

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Compliance
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Trust Center
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				A single view of the platform's security, privacy, and compliance
				posture. Every card pulls its data directly from the corresponding
				control surface, so the information here is always current.
			</p>
		</header>
	);
}

function Card({ card }: { card: TrustCardView }) {
	return (
		<article className={CARD} aria-labelledby={`trust-${card.id}-title`}>
			<header className="flex flex-col gap-1">
				<h2
					id={`trust-${card.id}-title`}
					className="text-base font-semibold text-zinc-900"
				>
					{card.title}
				</h2>
				<p className="text-xs text-zinc-500">{card.description}</p>
			</header>
			<dl className="flex flex-col gap-2">
				{card.fields.map((field) => (
					<div
						key={field.label}
						className="flex items-baseline justify-between gap-3 border-t border-zinc-100 pt-2 first:border-t-0 first:pt-0"
					>
						<dt className={FIELD_LABEL}>{field.label}</dt>
						<dd className={FIELD_VALUE}>{field.value}</dd>
					</div>
				))}
			</dl>
			<footer className="pt-1">
				<a
					href={card.detailsHref}
					className="inline-flex items-center gap-1 rounded-[6px] text-sm font-medium text-emerald-700 hover:text-emerald-800"
				>
					View details
					<span aria-hidden="true">{"\u2192"}</span>
				</a>
			</footer>
		</article>
	);
}

function PendingCard({
	title,
	description,
}: {
	title: string;
	description: string;
}) {
	return (
		<article
			className={CARD_PENDING}
			aria-busy="true"
			aria-label={`${title} loading`}
		>
			<header className="flex flex-col gap-1">
				<h2 className="text-base font-semibold text-zinc-600">{title}</h2>
				<p className="text-xs text-zinc-500">{description}</p>
			</header>
			<div className="flex flex-col gap-2" aria-hidden="true">
				<div className="h-3 w-1/2 rounded-[6px] bg-zinc-200" />
				<div className="h-3 w-3/4 rounded-[6px] bg-zinc-200" />
				<div className="h-3 w-2/3 rounded-[6px] bg-zinc-200" />
			</div>
			<p className="text-xs text-zinc-500" role="status">
				Loading
			</p>
		</article>
	);
}

function ErrorBanner({ message }: { message: string }) {
	return (
		<div
			className="flex flex-col gap-1 rounded-[8px] border border-red-200 bg-red-50 p-4"
			role="alert"
		>
			<p className="text-sm font-medium text-red-800">
				Unable to load Trust Center data.
			</p>
			<p className="text-xs text-red-700">{message}</p>
		</div>
	);
}

function FooterNote() {
	return (
		<aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
			<p className="font-medium text-zinc-800">About this page</p>
			<ul className="mt-2 flex list-inside list-disc flex-col gap-1">
				<li>
					Each card is fetched in parallel; a failure in one lane never blocks
					the rest of the hub.
				</li>
				<li>
					All figures reflect the current tenant only. Switch workspace to view
					another tenant's posture.
				</li>
				<li>Details links are placeholders until per-surface pages land.</li>
			</ul>
		</aside>
	);
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function describeError(err: unknown): string {
	if (err instanceof Error && err.message) return err.message;
	return "An unexpected error occurred while contacting the API.";
}
